<template>
  <div class="guest-login-container">
    <img
      v-if="showBackgroundImages"
      src="/images/background-cap.svg"
      alt=""
      aria-hidden="true"
      class="guest-login-background-cap"
    />
    <div class="guest-login-left-panel">
      <img
        v-if="showBackgroundImages"
        src="/images/white-bird.svg"
        class="guest-login-white-bird"
        alt=""
        aria-hidden="true"
      />
      <router-link v-if="showLogoLink" to="/" class="guest-login-logo-link" aria-label="Home page">
        <img src="/images/eddy.svg" alt="" aria-hidden="true" />
      </router-link>

      <div class="guest-login-title-container">
        <div class="guest-login-title-wrapper">
          <h1 class="guest-login-title">Continue as a Guest</h1>
        </div>
      </div>

      <div class="guest-login-form-inputs">
        <div class="guest-login-input-wrapper">
          <BaseInput
            type="text"
            label="Email*"
            placeholder="Enter your email to proceed without an account"
            v-model="email"
            autocomplete="email"
            class="guest-login-input-field"
            :error="isEmailError"
            :errorMessage="emailErrorMessage || errorMessage"
            :showInputError="false"
          />

          <div
            v-if="errorMessage && !isEmailError"
            class="guest-login-error-message"
            role="alert"
            aria-live="assertive"
          >
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="guest-login-action-buttons">
        <div class="guest-login-action-buttons-wrapper">
          <BaseButton
            :loading="loading"
            :disabled="loading"
            @click="continueAsGuest"
            variant="primary"
            class="guest-login-primary-button"
          >
            Continue as Guest
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- right side -->
    <div v-if="showRightPanel" class="guest-login-right-panel">
      <div v-if="showLanguageSelector" class="guest-login-language-toggle">
        <button
          type="button"
          class="guest-login-language-button guest-login-language-button-en"
          :class="{ active: selectedLanguage === 'EN' }"
          @click="setLanguage('EN')"
        >
          EN
        </button>
        <button
          type="button"
          class="guest-login-language-button guest-login-language-button-es"
          :class="{ active: selectedLanguage === 'ES' }"
          @click="setLanguage('ES')"
        >
          ES
        </button>
      </div>

      <div class="guest-login-signup-content">
        <img
          src="/images/login-illustration.svg"
          class="guest-login-illustration"
          alt=""
          aria-hidden="true"
        />
        <p class="guest-login-signup-title">Sign up for Eddy!</p>
        <button
          type="button"
          class="guest-login-signup-button"
          @click="goToSignup"
        >
          New to Eddy? Sign up here!
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";
import BaseButton from "@/components/common/BaseButton.vue";
import { validateEmail } from "../utils";
import BaseInput from "@/components/BaseInput.vue";

// Props
const props = defineProps({
  // Controls whether to show the right promotional panel
  showRightPanel: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show the logo link in top-left
  showLogoLink: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show language selector
  showLanguageSelector: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show background images
  showBackgroundImages: {
    type: Boolean,
    default: true,
  },
  // Controls whether to redirect after actions
  enableRedirect: {
    type: Boolean,
    default: true,
  },
});

// Emits
const emit = defineEmits([
  "guest-login-success",
  "guest-login-error",
  "goto-login",
  "goto-signup",
]);

const router = useRouter();

const email = ref("");
const selectedLanguage = ref("EN");
const loading = ref(false);
const isEmailError = ref(false);
const emailErrorMessage = ref("");
const errorMessage = ref("");

function setLanguage(lang) {
  selectedLanguage.value = lang;
}

async function continueAsGuest() {
  const { isValid, message } = validateEmail(email.value);
  if (!isValid) {
    isEmailError.value = true;
    emailErrorMessage.value = message;
    return;
  }

  isEmailError.value = false;
  emailErrorMessage.value = "";
  errorMessage.value = "";

  loading.value = true;

  try {
    // Simulate a brief loading state
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Store guest email locally
    localStorage.setItem("guestEmail", email.value);

    // Emit success event with just the email
    emit("guest-login-success", {
      email: email.value,
    });
  } catch (error) {
    const errorMsg = "Sorry, we can't process your request.";
    errorMessage.value = errorMsg;
    emit("guest-login-error", errorMsg);
  } finally {
    loading.value = false;
  }
}

function goToSignup() {
  emit("goto-signup");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/signup");
  }
}
</script>

<style scoped>
.guest-login-container {
  display: flex;
  height: 100vh;
}

.guest-login-background-cap {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: -999;
}

.guest-login-left-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.guest-login-white-bird {
  position: absolute;
  z-index: -1;
  bottom: 0;
  left: 0;
  width: 40%;
}

.guest-login-logo-link {
  display: flex;
  gap: 19px;
  margin-top: 40px;
  margin-left: 40px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 50;
}

.guest-login-title-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.guest-login-title-wrapper {
  width: 430px;
  display: flex;
  justify-content: start;
}

.guest-login-title {
  font-size: var(--fs-large);
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--primary-blue);
}

.guest-login-form-inputs {
  display: flex;
  gap: 20px;
  width: 100%;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}

.guest-login-input-wrapper {
  width: 430px;
}

:deep(.guest-login-input-field) {
  width: 430px !important;
  text-align: left;
}

:deep(.guest-login-input-field input) {
  width: 100% !important;
  text-align: left;
}

:deep(.guest-login-input-field .input-wrap) {
  width: 430px !important;
}

.guest-login-error-message {
  color: #e00269;
  font-size: var(--fs-small);
  margin-top: 10px;
  width: 430px;
}

.guest-login-action-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.guest-login-action-buttons-wrapper {
  display: flex;
  gap: 10px;
  flex-direction: column;
}

.guest-login-primary-button {
  width: 430px;
}

.guest-login-back-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.guest-login-right-panel {
  width: 50%;
  background-color: var(--primary-blue);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.guest-login-language-toggle {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 1rem;
  margin-top: 45px;
  padding-right: 40px;
  position: absolute;
  right: 0;
  top: 0;
}

.guest-login-language-button {
  padding: 8px;
  width: 60px;
  font-size: var(--fs-small);
  cursor: pointer;
  border: none;
  background: #f2f3fb;
  color: #e00069;
}

.guest-login-language-button-en {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.guest-login-language-button-es {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.guest-login-language-button.active {
  background: #e00069;
  color: white;
}

.guest-login-signup-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.guest-login-illustration {
  width: 50%;
}

.guest-login-signup-title {
  color: white;
  font-size: 32px;
  font-weight: 600;
  margin-top: 32px;
}

.guest-login-signup-button {
  font-size: var(--fs-big);
  font-weight: 600;
  color: var(--primary-blue);
  background-color: white;
  width: 480px;
  height: 48px;
  border-radius: 12px;
  margin-top: 32px;
  border: none;
  cursor: pointer;
}

.hover-underline:hover {
  text-decoration: underline;
}

.color-primary {
  color: var(--primary-blue);
}

@media (max-width: 768px) {
  .guest-login-container {
    height: fit-content;
  }

  .guest-login-right-panel {
    display: none;
  }

  .guest-login-left-panel {
    width: 100%;
  }

  .guest-login-action-buttons-wrapper {
    width: 100%;
  }

  .guest-login-logo-link {
    margin-left: 16px;
    margin-top: 16px;
  }

  .guest-login-title-wrapper {
    width: 100%;
  }

  .guest-login-input-wrapper {
    width: 100%;
  }

  :deep(.guest-login-input-field) {
    width: 100% !important;
  }

  :deep(.guest-login-input-field .input-wrap) {
    width: 100% !important;
  }

  .guest-login-error-message {
    width: 100%;
  }

  .guest-login-primary-button {
    width: 100%;
  }
}
</style>
